import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { FormFieldNewComponent } from '@sdvor/shared/ui';
import { SdvorButtonComponent, SdvorToggleComponent } from '@sdvor/shared/ui/v2';
import { ExtendedCustomFormValidators } from '@shared/helpers';
import { ButtonSizes, ButtonThemes } from '@shared/v2';
import { User, UserProfileFacade } from '@spartacus/user';
import { filter } from 'rxjs';

@Component({
  selector: 'sd-user-email-form',
  imports: [ReactiveFormsModule, FormFieldNewComponent, SdvorToggleComponent, SdvorButtonComponent],
  templateUrl: './user-email-form.component.html',
  styleUrl: './user-email-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEmailFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly userProfileFacade = inject(UserProfileFacade);

  protected buttonThemes = ButtonThemes;
  protected buttonSizes = ButtonSizes;

  protected form = this.formBuilder.nonNullable.group(
    {
      email: ['', [ExtendedCustomFormValidators.emailValidator, Validators.email]],
      marketingEmailsConsent: [false],
    },
    {
      validators: [this.formValidator],
    },
  );

  private initialValue = this.form.getRawValue();

  protected isFormEditing = signal(false);

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isFormEditing.set(true);
    });

    this.userProfileFacade
      .get()
      .pipe(
        filter((user): user is User => Boolean(user)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => {
        this.initialValue = {
          email: user.email ?? '',
          marketingEmailsConsent: user.marketingEmailsConsent ?? false,
        };

        this.form.setValue(this.initialValue, { emitEvent: false });
      });
  }

  protected onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();

      return;
    }

    this.isFormEditing.set(false);
    this.userProfileFacade.update(this.form.value).subscribe();
  }

  protected onReset(): void {
    this.form.setValue(this.initialValue, { emitEvent: false });
    this.isFormEditing.set(false);
  }

  /**
   * Если подписка на рассылку включена, но емайл введен не верно,
   * то считаем форму не валидной.
   */
  private formValidator(control: AbstractControl): ValidationErrors | null {
    if (
      !!control.get('marketingEmailsConsent')?.value &&
      (!control.get('email')?.value || control.get('email')?.invalid)
    ) {
      return { marketingEmailsConsent: true };
    }

    return null;
  }
}
